// OrderSummary.jsx
//
// Third tab in the Distributor Orders module (after Orders + Forecast
// Comparison). Renders a monthly shipment summary grid:
//
//   Rows (column 1): one band per bottle size the account sells, plus an
//     "All Cases" band. Within each band: one row per region (alphabetical)
//     and a subtotal row.
//   Columns (2+): one per period (month or quarter), each split into
//     Actual / Goal / % to Goal.
//     - Actual: sum of order_items.quantity across orders that are SHIPPED and
//       whose pickup_date falls in the period, scoped to the row's region (and
//       bottle size). Status and `visible` are both filtered server-side (see
//       loadShipmentRowsForRange) so this tab and the comparison tab agree.
//     - Goal:   editable target stored in `sales_goals`, one cell per real
//       size; subtotals + All-Cases are derived/read-only.
//
// The size bands come from the account's own Product SKUs (see
// utils/sizeGroups.js) — nothing here assumes a particular bottle format.
//
// Bucketing uses the actual pickup_date (not the planned target_ship_month).
// Editing the Goal column is gated by `canEdit` (any account member today;
// role-gating can layer on later, like the forecasting port).
//
// The spreadsheet GoalImportModal (bulk xlsx import) is deferred — the
// "Edit Goals" button toggles inline editing directly.

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  loadComparisonSupport,
  loadShipmentRowsForRange,
  loadSalesGoalsForRange,
  upsertSalesGoal,
} from "../utils/ordersService";
import OrderSummaryChart from "./OrderSummaryChart";
import { exportSummaryToExcel } from "./orderSummaryExport";
import { ALL_CASES_ID, buildSizeGroups, sizeIdsOf, skuSize } from "../utils/sizeGroups";
import "./OrderSummary.css";

const FULL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function addMonths(monthName, year, delta) {
  const idx        = FULL_MONTHS.indexOf(monthName);
  const total      = idx + delta;
  const newIdx     = ((total % 12) + 12) % 12;
  const yearDelta  = Math.floor(total / 12);
  return {
    month: FULL_MONTHS[newIdx],
    year:  year + yearDelta,
    abbr:  SHORT_MONTHS[newIdx],
    mnum:  newIdx + 1,
  };
}

function firstOfMonthIso(year, mnum) {
  return `${year}-${String(mnum).padStart(2, "0")}-01`;
}
function lastOfMonthIso(year, mnum) {
  const lastDay = new Date(year, mnum, 0).getDate();
  return `${year}-${String(mnum).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
}

const _now          = new Date();
const DEFAULT_MONTH = "January";
const DEFAULT_NUM_MONTHS = 12;
const DEFAULT_YEAR  = _now.getFullYear();
const YEAR_OPTIONS  = [DEFAULT_YEAR - 1, DEFAULT_YEAR, DEFAULT_YEAR + 1];

function fmtNum(n) {
  if (!n) return "—";
  return Math.round(n).toLocaleString();
}

function pctToGoal(actual, goal) {
  if (!goal || goal <= 0) return null;
  return (Number(actual) / Number(goal)) * 100;
}
function fmtPct(pct) {
  if (pct == null) return "—";
  return `${Math.round(pct).toLocaleString()}%`;
}
function pctClass(pct) {
  if (pct == null)  return "";
  if (pct >= 100)   return "os-pct-good";
  if (pct >= 75)    return "os-pct-warn";
  return "os-pct-bad";
}

const OrderSummary = forwardRef(function OrderSummary({ canEdit = false }, ref) {
  const [refMonth,    setRefMonth]    = useState(DEFAULT_MONTH);
  const [refYear,     setRefYear]     = useState(DEFAULT_YEAR);
  const [numMonths,   setNumMonths]   = useState(DEFAULT_NUM_MONTHS);
  const [fRegion,     setFRegion]     = useState("");
  const [viewMode,    setViewMode]    = useState("grid");
  const [periodMode,  setPeriodMode]  = useState("month");

  const [support,     setSupport]     = useState({ regions: [], stateRegionMap: {}, sizes: [] });
  const [orderRows,   setOrderRows]   = useState([]);
  const [goalRows,    setGoalRows]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const canEditGoals = !!canEdit;
  const [editingGoals,      setEditingGoals]      = useState(false);
  const [savingKey,         setSavingKey]         = useState(null);
  const [saveError,         setSaveError]         = useState(null);

  const months = useMemo(
    () => Array.from({ length: numMonths }, (_, i) => addMonths(refMonth, refYear, i)),
    [refMonth, refYear, numMonths]
  );

  const displayPeriods = useMemo(() => {
    if (periodMode === "month") {
      return months.map(m => ({
        id:          `${m.year}-${String(m.mnum).padStart(2, "0")}`,
        label:       `${m.abbr} ${m.year}`,
        members:     [{ year: m.year, mnum: m.mnum }],
        fullMembers: [{ year: m.year, mnum: m.mnum }],
        isQuarter:   false,
      }));
    }
    const byKey = new Map();
    for (const m of months) {
      const q     = Math.ceil(m.mnum / 3);
      const key   = `${m.year}-Q${q}`;
      if (!byKey.has(key)) {
        const firstMonthOfQ = (q - 1) * 3 + 1;
        const fullMembers = [
          { year: m.year, mnum: firstMonthOfQ     },
          { year: m.year, mnum: firstMonthOfQ + 1 },
          { year: m.year, mnum: firstMonthOfQ + 2 },
        ];
        byKey.set(key, {
          id:          key,
          label:       `Q${q} ${m.year}`,
          quarter:     q,
          year:        m.year,
          members:     [],
          fullMembers,
          isQuarter:   true,
        });
      }
      byKey.get(key).members.push({ year: m.year, mnum: m.mnum });
    }
    return [...byKey.values()];
  }, [months, periodMode]);

  useEffect(() => {
    loadComparisonSupport()
      .then(setSupport)
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (months.length === 0) { setOrderRows([]); setGoalRows([]); setLoading(false); return; }
    const startDate = firstOfMonthIso(months[0].year, months[0].mnum);
    const lastM     = months[months.length - 1];
    const endDate   = lastOfMonthIso(lastM.year, lastM.mnum);
    Promise.all([
      loadShipmentRowsForRange(startDate, endDate),
      loadSalesGoalsForRange(startDate, endDate),
    ])
      .then(([orders, goals]) => {
        if (cancelled) return;
        setOrderRows(orders || []);
        setGoalRows(goals || []);
      })
      .catch(err  => { if (!cancelled) setError(err.message); })
      .finally(()  => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refMonth, refYear, numMonths]);

  // One band per size the account actually sells, plus the All-Cases total.
  const sizeGroups = useMemo(() => buildSizeGroups(support.sizes), [support.sizes]);
  const realSizeIds = useMemo(() => sizeIdsOf(sizeGroups), [sizeGroups]);

  const cellMap = useMemo(() => {
    const m = new Map();
    for (const item of orderRows) {
      const order = item.order;
      if (!order) continue;
      const region = support.stateRegionMap[order.state];
      if (!region) continue;
      const pickup = order.pickup_date;
      if (!pickup) continue;
      const bucketYm = String(pickup).slice(0, 7);
      const qty      = Number(item.quantity) || 0;
      if (qty <= 0) continue;
      const size     = skuSize(item.sku_name);
      for (const group of sizeGroups) {
        if (group.sizeMatch !== null && size !== group.sizeMatch) continue;
        const k = `${group.id}|${region}|${bucketYm}`;
        m.set(k, (m.get(k) || 0) + qty);
      }
    }
    return m;
  }, [orderRows, support.stateRegionMap, sizeGroups]);

  const [goalMap, setGoalMap] = useState(new Map());
  useEffect(() => {
    const m = new Map();
    for (const r of goalRows) {
      const k = `${r.region}|${r.bottle_size}|${r.year}-${String(r.month).padStart(2, "0")}`;
      m.set(k, Number(r.goal_cases) || 0);
    }
    setGoalMap(m);
  }, [goalRows]);

  const displayRegions = fRegion ? [fRegion] : support.regions;

  const cellKey = (year, mnum) => `${year}-${String(mnum).padStart(2, "0")}`;
  const regionCell = (groupId, region, year, mnum) =>
    cellMap.get(`${groupId}|${region}|${cellKey(year, mnum)}`) || 0;

  const goalCell = (groupId, region, year, mnum) => {
    const ck = cellKey(year, mnum);
    if (groupId === ALL_CASES_ID) {
      // All Cases is derived: the sum of every real size's goal.
      return realSizeIds.reduce((s, id) => s + (goalMap.get(`${region}|${id}|${ck}`) || 0), 0);
    }
    return goalMap.get(`${region}|${groupId}|${ck}`) || 0;
  };

  const regionCellForPeriod = (groupId, region, period) =>
    period.members.reduce((s, m) => s + regionCell(groupId, region, m.year, m.mnum), 0);
  const goalCellForPeriod = (groupId, region, period) =>
    period.members.reduce((s, m) => s + goalCell(groupId, region, m.year, m.mnum), 0);
  const subtotalForGroupPeriod = (groupId, period) => {
    let sum = 0;
    for (const region of displayRegions) sum += regionCellForPeriod(groupId, region, period);
    return sum;
  };
  const subtotalGoalForGroupPeriod = (groupId, period) => {
    let sum = 0;
    for (const region of displayRegions) sum += goalCellForPeriod(groupId, region, period);
    return sum;
  };

  // Every real size is directly editable; All Cases is a derived total.
  const isCellEditable = (groupId) =>
    editingGoals && periodMode === "month" && groupId !== ALL_CASES_ID;

  const saveGoalCell = async (region, bottleSize, period, rawValue) => {
    const num = rawValue === "" || rawValue == null ? 0 : Number(rawValue);
    if (!Number.isFinite(num) || num < 0) return;
    const targets = period.fullMembers;
    const base    = Math.floor(num / targets.length);
    const remainder = num - base * targets.length;
    const perMonth = targets.map((t, i) => ({
      ...t,
      goal: base + (i === targets.length - 1 ? remainder : 0),
    }));
    const editKey = `${region}|${bottleSize}|${period.id}`;

    const priorVals = perMonth.map(t => ({
      key: `${region}|${bottleSize}|${cellKey(t.year, t.mnum)}`,
      val: goalMap.get(`${region}|${bottleSize}|${cellKey(t.year, t.mnum)}`) || 0,
    }));
    const noChange = priorVals.every((p, i) => p.val === perMonth[i].goal);
    if (noChange) return;

    setGoalMap(prev => {
      const next = new Map(prev);
      for (const t of perMonth) next.set(`${region}|${bottleSize}|${cellKey(t.year, t.mnum)}`, t.goal);
      return next;
    });
    setSavingKey(editKey);
    setSaveError(null);
    try {
      for (const t of perMonth) {
        // eslint-disable-next-line no-await-in-loop
        await upsertSalesGoal({ region, bottleSize, year: t.year, month: t.mnum, goalCases: t.goal });
      }
    } catch (err) {
      setSaveError(`Save failed for ${region} ${bottleSize} ${period.label}: ${err.message}`);
      setGoalMap(prev => {
        const next = new Map(prev);
        for (const p of priorVals) next.set(p.key, p.val);
        return next;
      });
    } finally {
      setSavingKey(prev => (prev === editKey ? null : prev));
    }
  };

  useImperativeHandle(ref, () => ({
    exportToExcel: async () => {
      try {
        await exportSummaryToExcel({
          periodMode,
          periods:     displayPeriods,
          regions:     displayRegions,
          sizeGroups:  sizeGroups,
          getActual:        regionCellForPeriod,
          getGoal:          goalCellForPeriod,
          subtotalActual:   subtotalForGroupPeriod,
          subtotalGoal:     subtotalGoalForGroupPeriod,
          filterSummary: { year: refYear, startMonth: refMonth, numMonths, region: fRegion, periodMode },
        });
      } catch (err) {
        setSaveError(`Export failed: ${err.message}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [periodMode, displayPeriods, displayRegions, refYear, refMonth, numMonths, fRegion, cellMap, goalMap]);

  return (
    <div className="os-wrap">
      <div className="os-filter-bar">
        <div className="os-filter-group">
          <label className="os-filter-label">Year</label>
          <select className="os-filter-select" value={refYear} onChange={e => setRefYear(Number(e.target.value))}>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="os-filter-group">
          <label className="os-filter-label">Starting Month</label>
          <select className="os-filter-select" value={refMonth} onChange={e => setRefMonth(e.target.value)}>
            {FULL_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="os-filter-group">
          <label className="os-filter-label">Number of Months</label>
          <select className="os-filter-select" value={numMonths} onChange={e => setNumMonths(Number(e.target.value))}>
            {Array.from({ length: 11 }, (_, i) => i + 2).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="os-filter-group">
          <label className="os-filter-label">Region</label>
          <select className="os-filter-select" value={fRegion} onChange={e => setFRegion(e.target.value)}>
            <option value="">All Regions</option>
            {support.regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {fRegion && (
          <button type="button" className="os-clear-btn" onClick={() => setFRegion("")} title="Clear region filter">
            Clear
          </button>
        )}

        <div className="os-period-toggle">
          <button
            type="button"
            className={`os-period-btn${periodMode === "month" ? " os-period-btn-active" : ""}`}
            onClick={() => setPeriodMode("month")}
            title="Show one column per month"
          >
            Monthly
          </button>
          <button
            type="button"
            className={`os-period-btn${periodMode === "quarter" ? " os-period-btn-active" : ""}`}
            onClick={() => { setPeriodMode("quarter"); setEditingGoals(false); }}
            title="Show one column per quarter"
          >
            Quarterly
          </button>
        </div>

        {canEditGoals && viewMode === "grid" && periodMode === "month" && (
          <button
            type="button"
            className={`os-edit-goals-btn${editingGoals ? " os-edit-goals-btn-active" : ""}`}
            onClick={() => setEditingGoals(v => !v)}
            title={editingGoals ? "Lock goal cells" : "Edit goal cells inline"}
          >
            {editingGoals ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Done Editing
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit Goals
              </>
            )}
          </button>
        )}

        {loading && <span className="os-loading-pill">Loading…</span>}
        {error && <span className="os-error-pill">{error}</span>}
        {saveError && <span className="os-error-pill">{saveError}</span>}

        <div className="os-view-toggle">
          <button
            className={`os-view-btn${viewMode === "grid" ? " os-view-btn-active" : ""}`}
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            Grid
          </button>
          <button
            className={`os-view-btn${viewMode === "chart" ? " os-view-btn-active" : ""}`}
            onClick={() => setViewMode("chart")}
            title="Chart view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Chart
          </button>
        </div>
      </div>

      {viewMode === "chart" ? (
        <OrderSummaryChart
          orderRows={orderRows}
          stateRegionMap={support.stateRegionMap}
          regions={support.regions}
          periods={displayPeriods}
          periodMode={periodMode}
          fRegion={fRegion}
          sizes={support.sizes}
        />
      ) : (
      <div className="os-grid-scroll">
        <table className="os-table">
          <thead>
            <tr>
              <th className="os-row-th os-row-th-top" rowSpan={2}>&nbsp;</th>
              {displayPeriods.map(p => (
                <th key={p.id} className="os-month-th" colSpan={3}>{p.label}</th>
              ))}
            </tr>
            <tr>
              {displayPeriods.map(p => (
                <React.Fragment key={`sub-${p.id}`}>
                  <th className="os-sub-th os-sub-th-actual">Actual</th>
                  <th className="os-sub-th os-sub-th-goal">Goal</th>
                  <th className="os-sub-th os-sub-th-pct">% to Goal</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sizeGroups.map(group => {
              const totalCols = 1 + displayPeriods.length * 3;
              return (
                <React.Fragment key={group.id}>
                  <tr className="os-group-header-row">
                    <td colSpan={totalCols} className="os-group-header-cell">
                      <span className="os-group-header-text">
                        {group.label === "All" ? "All Cases" : `${group.label} bottles`}
                      </span>
                    </td>
                  </tr>
                  {displayRegions.map((region, rIdx) => (
                    <tr key={`${group.id}-${region}`} className={`os-data-row${rIdx % 2 === 1 ? " os-row-alt" : ""}`}>
                      <td className="os-row-cell">{region}</td>
                      {displayPeriods.map(p => {
                        const editable   = isCellEditable(group.id);
                        const actualVal  = regionCellForPeriod(group.id, region, p);
                        const goalVal    = goalCellForPeriod(group.id, region, p);
                        const pct        = pctToGoal(actualVal, goalVal);
                        const savingThis = savingKey === `${region}|${group.id}|${p.id}`;
                        return (
                          <React.Fragment key={`${group.id}-${region}-${p.id}`}>
                            <td className="os-data-cell os-cell-actual">{fmtNum(actualVal)}</td>
                            <td className={`os-data-cell os-cell-goal${editable ? " os-cell-goal-editable" : ""}${savingThis ? " os-cell-goal-saving" : ""}`}>
                              {editable ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className="os-goal-input"
                                  defaultValue={goalVal || ""}
                                  placeholder="0"
                                  onBlur={e => saveGoalCell(region, group.id, p, e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === "Enter") e.target.blur();
                                    if (e.key === "Escape") { e.target.value = goalVal || ""; e.target.blur(); }
                                  }}
                                />
                              ) : (
                                fmtNum(goalVal)
                              )}
                            </td>
                            <td className={`os-data-cell os-cell-pct ${pctClass(pct)}`}>{fmtPct(pct)}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className={`os-subtotal-row${group.id === "all_cases" ? " os-grand-total-row" : ""}`}>
                    <td className="os-row-cell os-subtotal-label">{group.subtotalLabel}</td>
                    {displayPeriods.map(p => {
                      const actualSum = subtotalForGroupPeriod(group.id, p);
                      const goalSum   = subtotalGoalForGroupPeriod(group.id, p);
                      const pct       = pctToGoal(actualSum, goalSum);
                      return (
                        <React.Fragment key={`${group.id}-total-${p.id}`}>
                          <td className="os-data-cell os-subtotal-cell os-cell-actual">{fmtNum(actualSum)}</td>
                          <td className="os-data-cell os-subtotal-cell os-cell-goal">{fmtNum(goalSum)}</td>
                          <td className={`os-data-cell os-subtotal-cell os-cell-pct ${pctClass(pct)}`}>{fmtPct(pct)}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}

            {!loading && displayRegions.length === 0 && (
              <tr>
                <td colSpan={1 + displayPeriods.length * 3} className="os-empty-cell">
                  {support.regions.length === 0
                    ? "No regions configured — set them up in System Settings → States."
                    : "No regions match the current filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
});

export default OrderSummary;
